export const WATERMARK_ROTATION_DEG = -30;
/** Preferred font size: ~17% of the shorter tile edge */
export const FONT_SIZE_RATIO = 0.17;
/** Average character width for bold serif, as a fraction of font size */
export const CHAR_WIDTH_RATIO = 0.58;
/** Share of each tile edge the rotated label may span, so it never reaches the edges */
export const TILE_FILL_RATIO = 0.9;
/**
 * Proportions of the printed tile, which is drawn at the full paper width.
 * Fixed rather than measured, so print does not depend on the screen it was
 * started from, and at most half a printed page tall, so every sheet holds at
 * least one whole label.
 */
export const WATERMARK_PRINT_TILE = { width: 190, height: 100 } as const;

export type WatermarkTileOptions = {
  label: string;
  width: number;
  height: number;
};

export type WatermarkLabelExtent = {
  width: number;
  height: number;
};

const escapeXml = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");

const ROTATION_COS = Math.abs(
  Math.cos((WATERMARK_ROTATION_DEG * Math.PI) / 180)
);
const ROTATION_SIN = Math.abs(
  Math.sin((WATERMARK_ROTATION_DEG * Math.PI) / 180)
);

/**
 * Rendered label length. The SVG pins it with `textLength`, so the label is
 * exactly this long whichever serif the image falls back to (an SVG used as a
 * background image cannot load the page's web fonts).
 */
export const getWatermarkTextLength = (
  label: string,
  fontSize: number
): number => Math.max(label.trim().length, 1) * fontSize * CHAR_WIDTH_RATIO;

/** Axis-aligned bounding box of the rotated label. */
export const getWatermarkLabelExtent = (
  label: string,
  fontSize: number
): WatermarkLabelExtent => {
  const length = getWatermarkTextLength(label, fontSize);
  return {
    width: length * ROTATION_COS + fontSize * ROTATION_SIN,
    height: length * ROTATION_SIN + fontSize * ROTATION_COS,
  };
};

/**
 * The preferred size, shrunk until the rotated label fits inside the tile:
 * long labels such as "Ikke-gældende" would otherwise run past the tile edges
 * and be cut off.
 */
export const getWatermarkFontSize = (
  label: string,
  width: number,
  height: number
): number => {
  const preferred = Math.min(width, height) * FONT_SIZE_RATIO;
  const extentPerUnit = getWatermarkLabelExtent(label, 1);
  const fitsWidth = (width * TILE_FILL_RATIO) / extentPerUnit.width;
  const fitsHeight = (height * TILE_FILL_RATIO) / extentPerUnit.height;
  return Math.min(preferred, fitsWidth, fitsHeight);
};

/**
 * One label centred in the tile. The tile repeats down the document, so each
 * screen-height of text carries one whole label.
 */
export const buildWatermarkTileSvg = ({
  label,
  width,
  height,
}: WatermarkTileOptions): string => {
  const fontSize = getWatermarkFontSize(label, width, height);
  const textLength = getWatermarkTextLength(label, fontSize);
  const x = width / 2;
  const y = height / 2;
  const escapedLabel = escapeXml(label.trim());

  const text = `<text x="${x}" y="${y}" font-family="Gelasio, Georgia, serif" font-size="${fontSize}" font-weight="700" text-anchor="middle" dominant-baseline="middle" textLength="${textLength}" lengthAdjust="spacingAndGlyphs" fill="currentColor" fill-opacity="0.15" transform="rotate(${WATERMARK_ROTATION_DEG} ${x} ${y})">${escapedLabel}</text>`;

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">${text}</svg>`;
};

export const buildWatermarkTileDataUrl = (
  options: WatermarkTileOptions
): string => {
  const svg = buildWatermarkTileSvg(options);
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
};
